import { useEffect, useState } from "react";

import type { AdminListPagination, AdminReportDetailResponseData, AdminReportSummary } from "@chaochud/contracts";

import { CriticalActionForm, DataTable, PageHeader, StatusPill } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  OPEN: "warning",
  ASSIGNED: "warning",
  RESOLVED: "success",
  REJECTED: "neutral",
};

export function ReportsPage() {
  const [items, setItems] = useState<AdminReportSummary[]>([]);
  const [pagination, setPagination] = useState<AdminListPagination | null>(null);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AdminReportDetailResponseData | null>(null);

  const load = async (targetPage = page) => {
    const data = await adminOpsClient.listReports(targetPage);
    setItems(data.items);
    setPagination(data.pagination);
  };

  useEffect(() => {
    void load(page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader title="จัดการข้อพิพาท" />
      <DataTable
        headers={["เป้าหมาย", "เหตุผล", "สถานะ", "สร้างเมื่อ", ""]}
        rows={items.map((report) => [
          `${report.targetType}:${report.targetId.slice(0, 8)}`,
          report.reasonCode,
          <StatusPill key={`status-${report.id}`} tone={STATUS_TONE[report.status] ?? "neutral"}>
            {report.status}
          </StatusPill>,
          new Date(report.createdAt).toLocaleString("th-TH"),
          <button
            key={report.id}
            type="button"
            className="text-sm font-semibold text-plum hover:underline"
            onClick={async () => setDetail(await adminOpsClient.getReport(report.id))}
          >
            เปิด
          </button>,
        ])}
        pagination={pagination ?? undefined}
        onPageChange={setPage}
      />
      {detail ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-line bg-slate-surface p-4 text-sm text-ink">
            <p className="font-medium">
              {detail.targetType} / {detail.targetId}
            </p>
            <p>{detail.description ?? "-"}</p>
            <p className="text-xs text-muted">
              evidence keys: {detail.evidenceFileKeys.join(", ") || "-"}
            </p>
            <button
              type="button"
              className="mt-2 text-sm font-semibold text-plum hover:underline"
              onClick={async () => {
                await adminOpsClient.assignReport(detail.id);
                setDetail(await adminOpsClient.getReport(detail.id));
                await load();
              }}
            >
              Assign ให้ฉัน
            </button>
          </div>
          <CriticalActionForm
            title="Resolve รายงาน"
            targetLabel={detail.id}
            currentState={detail.status}
            expectedState="RESOLVED"
            impact="ปิดเคสหลังดำเนินการแล้ว"
            submitLabel="Resolve"
            onSubmit={async (payload) => {
              await adminOpsClient.resolveReport(detail.id, payload);
              setDetail(await adminOpsClient.getReport(detail.id));
              await load();
            }}
          />
          <CriticalActionForm
            title="Reject รายงาน"
            targetLabel={detail.id}
            currentState={detail.status}
            expectedState="REJECTED"
            impact="ปฏิเสธเคสว่าไม่เข้าเกณฑ์"
            submitLabel="Reject"
            onSubmit={async (payload) => {
              await adminOpsClient.rejectReport(detail.id, payload);
              setDetail(await adminOpsClient.getReport(detail.id));
              await load();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
